import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    ArrowLeft,
    MapPin,
    Bed,
    Bath,
    Car,
    FileText,
    Eye,
    Phone,
    MessageCircle,
    Mail,
    Calendar,
    Home,
    Ruler,
    Share2,
    Heart,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { getPublicPropertyById } from "../../api/publicApi";

export default function PropertyViewListingDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);

    // Format description with proper paragraphs and spacing
    const formatDescription = (description) => {
        if (!description) return ['No description available for this property.'];

        // Split by double line breaks first (if they exist)
        let paragraphs = description.split(/\n\n+/);

        // If no double line breaks, split by single line breaks
        if (paragraphs.length === 1) {
            paragraphs = description.split(/\n/);
        }

        // If still no breaks, try to split by periods followed by capital letters (new sentences)
        if (paragraphs.length === 1) {
            // Split on ". " followed by capital letter, but keep the period
            paragraphs = description.split(/\.\s+(?=[A-Z])/);
            paragraphs = paragraphs.map(p => p.trim() + (p.endsWith('.') ? '' : '.'));
        }

        // Clean up and filter empty paragraphs
        paragraphs = paragraphs
            .map(p => p.trim())
            .filter(p => p.length > 0);

        return paragraphs.length > 0 ? paragraphs : ['No description available for this property.'];
    };

    // ✅ Fetch property details
    useEffect(() => {
        const fetchPropertyDetail = async () => {
            try {
                setLoading(true);
                const res = await getPublicPropertyById(id);
                console.log("Property data received:", res.data);
                setProperty(res.data);
            } catch (err) {
                console.error("Error fetching property detail:", err);
                toast.error("Failed to load property details.");
                setError("Failed to load property details.");
            } finally {
                setLoading(false);
            }
        };

        fetchPropertyDetail();
    }, [id]);

    // ✅ Handle Contact Agent
    const handleContactAgent = () => {
        navigate("/contact-agent", {
            state: { property: property }
        });
    };

    // Handle image gallery navigation
    const nextImage = () => {
        if (images && images.length > 0) {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }
    };

    const prevImage = () => {
        if (images && images.length > 0) {
            setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    };

    // Handle share functionality
    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: title || "Property Listing",
                text: `Check out this property: ${title}`,
                url: window.location.href,
            }).catch(() => {
                // User cancelled or error occurred
            });
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(window.location.href);
            toast.success("Link copied to clipboard!");
        }
    };

    // Handle favorite toggle
    const toggleFavorite = () => {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? "Removed from favorites" : "Added to favorites!");
    };


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-gray-600 text-lg font-medium">
                Loading property details...
            </div>
        );
    }

    if (error || !property) {
        return (
            <div className="min-h-screen flex items-center justify-center text-red-600 font-semibold">
                {error || "Property not found."}
            </div>
        );
    }

    const {
        title,
        address,
        city,
        state,
        postal_code,
        price,
        beds,
        baths,
        parkings,
        area,
        description,
        property_type,
        status,
        created_at,
        images,
        agent,
    } = property;

    return (
        <div className="min-h-screen bg-white">
            {/* Back Button */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-[#55C5D0] transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="font-medium">Back</span>
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <span
                                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${status?.toLowerCase().includes("sale") || status?.toLowerCase().includes("residential")
                                                ? "bg-green-100 text-green-700"
                                                : "bg-yellow-100 text-yellow-700"
                                            }`}
                                    >
                                        {status || "rental"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={toggleFavorite}
                                        className={`p-2 rounded-lg border transition-colors ${isFavorite
                                                ? "bg-red-50 border-red-200 text-red-600"
                                                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                                            }`}
                                    >
                                        <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
                                    </button>
                                    <button
                                        onClick={handleShare}
                                        className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                                    >
                                        <Share2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                {title || "Untitled Property"}
                            </h1>
                            <div className="flex items-center gap-2 text-gray-600 mb-4">
                                <MapPin className="w-5 h-5 text-[#55C5D0]" />
                                <p className="text-lg">
                                    {address && city
                                        ? `${address}, ${city}, ${state} ${postal_code}`
                                        : "No address available"}
                                </p>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>
                                        Listed: {created_at ? new Date(created_at).toLocaleDateString() : "N/A"}
                                    </span>
                                </div>
                                {agent?.name && (
                                    <div className="flex items-center gap-1">
                                        <span>Agent: {agent.name}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="text-left lg:text-right">
                            {price && Number(price) > 0 ? (
                                <>
                                    <p className="text-4xl font-bold text-gray-900">
                                        ${Number(price).toLocaleString()}
                                    </p>
                                    {status?.toLowerCase().includes("rental") && (
                                        <p className="text-sm text-gray-500 mt-1">per month</p>
                                    )}
                                </>
                            ) : (
                                <p className="text-3xl font-bold text-gray-500">$N/A</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Images and Description */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Image Gallery */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="relative">
                                <div className="w-full h-[400px] sm:h-[500px] bg-gray-900">
                                    {images && images.length > 0 ? (
                                        <>
                                            <img
                                                src={images[currentImageIndex]?.file}
                                                alt={`${title} - Image ${currentImageIndex + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                            {images.length > 1 && (
                                                <>
                                                    <button
                                                        onClick={prevImage}
                                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                                                    >
                                                        <ChevronLeft className="w-6 h-6 text-gray-900" />
                                                    </button>
                                                    <button
                                                        onClick={nextImage}
                                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
                                                    >
                                                        <ChevronRight className="w-6 h-6 text-gray-900" />
                                                    </button>
                                                    <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                                                        {currentImageIndex + 1} / {images.length}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-gray-400">
                                            <div className="text-center">
                                                <Home className="w-16 h-16 mx-auto mb-2 opacity-50" />
                                                <p>No images available</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Thumbnail Strip */}
                                {images && images.length > 1 && (
                                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                                        <div className="flex gap-3 overflow-x-auto">
                                            {images.map((image, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === index
                                                            ? "border-[#55C5D0] ring-2 ring-[#55C5D0] ring-opacity-50"
                                                            : "border-gray-300 hover:border-gray-400"
                                                        }`}
                                                >
                                                    <img
                                                        src={image?.file}
                                                        alt={`Thumbnail ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Key Features */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Key Features</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="text-center p-4 bg-gray-50 rounded-lg">
                                    <Bed className="w-8 h-8 mx-auto mb-2 text-[#55C5D0]" />
                                    <p className="text-2xl font-bold text-gray-900">
                                        {beds ? (typeof beds === 'string' ? parseInt(beds) || beds : beds) : 0}
                                    </p>
                                    <p className="text-sm text-gray-600">Bedrooms</p>
                                </div>
                                <div className="text-center p-4 bg-gray-50 rounded-lg">
                                    <Bath className="w-8 h-8 mx-auto mb-2 text-[#55C5D0]" />
                                    <p className="text-2xl font-bold text-gray-900">
                                        {baths ? (typeof baths === 'string' ? parseInt(baths) || baths : baths) : 0}
                                    </p>
                                    <p className="text-sm text-gray-600">Bathrooms</p>
                                </div>
                                <div className="text-center p-4 bg-gray-50 rounded-lg">
                                    <Car className="w-8 h-8 mx-auto mb-2 text-[#55C5D0]" />
                                    <p className="text-2xl font-bold text-gray-900">
                                        {parkings ? (typeof parkings === 'string' ? parseInt(parkings) || parkings : parkings) : 0}
                                    </p>
                                    <p className="text-sm text-gray-600">Parking</p>
                                </div>
                                <div className="text-center p-4 bg-gray-50 rounded-lg">
                                    <Ruler className="w-8 h-8 mx-auto mb-2 text-[#55C5D0]" />
                                    <p className="text-2xl font-bold text-gray-900">
                                        {area ? (typeof area === 'number' ? area.toFixed(0) : area) : "N/A"}
                                    </p>
                                    <p className="text-sm text-gray-600">Land Size m²</p>
                                </div>
                            </div>
                        </div>

                        {/* Property Description */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">
                                Property Description
                            </h2>
                            <div className="text-gray-700 leading-relaxed space-y-4">
                                {formatDescription(description).map((paragraph, index) => (
                                    <p key={index} className="text-gray-700 leading-7">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Details and Agent */}
                    <div className="space-y-6">
                        {/* Contact Agent Card */}
                        {agent && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Agent</h3>
                                <div className="text-center mb-4">
                                    <div className="w-20 h-20 mx-auto mb-3 bg-[#55C5D0] rounded-full flex items-center justify-center">
                                        <span className="text-2xl font-bold text-white">
                                            {agent.name?.charAt(0)?.toUpperCase()}
                                        </span>
                                    </div>
                                    <h4 className="font-bold text-gray-900 text-lg">{agent.name}</h4>
                                    {agent.email && (
                                        <p className="text-sm text-gray-600 mt-1">{agent.email}</p>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    <button
                                        onClick={handleContactAgent}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#55C5D0] text-white rounded-lg hover:bg-[#48b5c0] transition-colors font-medium"
                                    >
                                        <MessageCircle className="w-5 h-5" />
                                        Get in Touch
                                    </button>
                                    {agent.email && (
                                        <a
                                            href={`mailto:${agent.email}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.location.href = `mailto:${agent.email}`;
                                            }}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                        >
                                            <Mail className="w-5 h-5" />
                                            Email Agent
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Property Details */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Property Details</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Property Type</span>
                                    <span className="font-semibold text-gray-900">{property_type || "N/A"}</span>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Bedrooms</span>
                                    <span className="font-semibold text-gray-900">
                                        {beds ? (typeof beds === 'string' ? parseInt(beds) || beds : beds) : 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Bathrooms</span>
                                    <span className="font-semibold text-gray-900">
                                        {baths ? (typeof baths === 'string' ? parseInt(baths) || baths : baths) : 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Parking Spaces</span>
                                    <span className="font-semibold text-gray-900">
                                        {parkings ? (typeof parkings === 'string' ? parseInt(parkings) || parkings : parkings) : 0}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Land Size</span>
                                    <span className="font-semibold text-gray-900">
                                        {area ? (typeof area === 'number' ? `${area.toFixed(0)} m²` : `${area} m²`) : "N/A"}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between py-3">
                                    <span className="text-gray-600 font-medium">Status</span>
                                    <span className="font-semibold text-gray-900">{status || "Available"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info Card */}
                        <div className="bg-gradient-to-br from-[#55C5D0] to-[#48b5c0] rounded-xl shadow-sm p-6 text-white">
                            <h3 className="text-lg font-bold mb-3">Need Help?</h3>
                            <p className="text-sm text-white/90 mb-4">
                                Our property experts are here to assist you with any questions about this listing.
                            </p>
                            <button
                                onClick={handleContactAgent}
                                className="w-full bg-white text-[#55C5D0] px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            >
                                Contact Us Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

